import { type ReactNode, useContext, useEffect, useState } from 'react';
import useConstant from 'use-constant';
import { sendWarning } from '@components/Noti';
import ValidatorContext from './ValidatorContext';

interface Params<T> {
  value: T;
  warning: boolean;
}

interface Props<T> {
  value: T;
  validFn: (value: T) => boolean;
  warningMessage?: string;
  warningHandler?: (message: string) => void;
  children: ({ value, warning }: Params<T>) => ReactNode;
}

function Validator<T>({
  value,
  validFn,
  warningMessage,
  warningHandler = sendWarning,
  children,
}: Props<T>) {
  const id = useConstant(() => Symbol('validatorItem'));
  const [warning, setWarning] = useState(false);
  const { addValidator, removeValidator } = useContext(ValidatorContext);

  useEffect(() => {
    return () => {
      removeValidator(id);
    };
  }, []);

  useEffect(() => {
    const fn = () => {
      const isValid = validFn(value);
      if (!isValid) {
        setWarning(true);
        if (warningMessage !== undefined) {
          warningHandler(warningMessage);
        }
      }
      return isValid;
    };
    addValidator(id, fn);
  }, [value, validFn, warningMessage, warningHandler]);

  useEffect(() => {
    setWarning(false);
  }, [value]);

  return <>{children({ value, warning })}</>;
}

export default Validator;
